/**
 * SyncSuggestionCardList - Card-based view for sync suggestions
 *
 * Features:
 * - Flattens suggestion sets into individual suggestions
 * - Filters to only show suggestions for visible entities
 * - Renders ToFilevineSuggestionCard for TO_FILEVINE direction
 * - Renders ToFinchSuggestionCard for TO_FINCH direction
 * - Animations via Framer Motion
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FinchEntity,
  useEntitySyncAcceptDataSuggestionMutation,
  useEntitySyncApplyDataSuggestionMutation,
  useEntitySyncListDataSuggestionsV2Query,
  useEntitySyncRejectDataSuggestionMutation,
} from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineEntitySyncSuggestionSchema,
  FilevineSectionConfigDataSchema,
  FilevineSectionFieldConfigSchema,
  FilevineSectionPayload,
} from '@/services/api/generated/entitySyncService';

import { usePolling, usePollingWhile } from '../context';
import type { CanonicalSectionType } from '../finch-cards';
import { ToFilevineSuggestionCard } from './ToFilevineSuggestionCard';
import { ToFinchSuggestionCard } from './ToFinchSuggestionCard';
import { useActionableSuggestions } from './useActionableSuggestions';

// ============================================================================
// Types
// ============================================================================

interface SyncSuggestionCardListProps {
  matterId: string;
  entityType: string;
  canonicalSectionType: CanonicalSectionType;
  firmId?: string;
  finchEntities?: FinchEntity[];
  filevineEntities?: DetailedFilevineCollectionItemSchema[];
  fieldConfigs?: FilevineSectionFieldConfigSchema[];
  sectionConfigData?: FilevineSectionConfigDataSchema;
  approvedEntityLinkIds?: Set<string>;
  autoPushedOnly?: boolean;
  reviewStatus?: 'PENDING_REVIEW' | 'ACCEPTED' | 'MODIFIED';
  includeReviewedAutoPushed?: boolean;
  hideHeader?: boolean;
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const SyncSuggestionCardList: React.FC<SyncSuggestionCardListProps> = ({
  matterId,
  entityType,
  canonicalSectionType,
  firmId,
  finchEntities = [],
  filevineEntities = [],
  fieldConfigs = [],
  sectionConfigData,
  approvedEntityLinkIds,
  autoPushedOnly,
  reviewStatus,
  includeReviewedAutoPushed = false,
  hideHeader = false,
  className,
}) => {
  // Get polling state from context
  const { pollingInterval, startPolling } = usePolling();

  // Fetch sync suggestions - uses polling interval from context
  const {
    data: syncSuggestionsData,
    isLoading,
    isError,
    refetch,
  } = useEntitySyncListDataSuggestionsV2Query(
    {
      matterId,
      entityType,
      autoPushedOnly,
      reviewStatus,
    },
    {
      pollingInterval,
      skipPollingIfUnfocused: true,
    },
  );

  const isAutoPushPendingReview = (suggestion: FilevineEntitySyncSuggestionSchema) => {
    const withReview = suggestion as FilevineEntitySyncSuggestionSchema & {
      is_auto_pushed?: boolean;
      review_status?: string | null;
    };
    return (
      withReview.is_auto_pushed === true &&
      suggestion.status === 'COMPLETED' &&
      withReview.review_status === 'PENDING_REVIEW'
    );
  };

  // Check if any suggestions are in progress and enable polling
  const hasInProgressSuggestions = useMemo(() => {
    const sets = syncSuggestionsData?.suggestion_sets ?? [];
    return sets.some((set) =>
      (set.suggestions ?? []).some(
        (s) => s.status === 'ACCEPTED' || s.status === 'EXECUTING',
      ),
    );
  }, [syncSuggestionsData?.suggestion_sets]);

  // Enable polling when suggestions are in progress
  usePollingWhile(hasInProgressSuggestions);

  // Mutations
  const [acceptSuggestion] = useEntitySyncAcceptDataSuggestionMutation();
  const [applySuggestion] = useEntitySyncApplyDataSuggestionMutation();
  const [rejectSuggestion] = useEntitySyncRejectDataSuggestionMutation();

  // Create lookup maps and ID sets for entities
  const {
    finchEntitiesMap,
    filevineEntitiesMap,
    visibleFinchIds,
    visibleFilevineIds,
  } = useMemo(() => {
    const finchMap = new Map(finchEntities.map((e) => [e.id, e]));
    const filevineMap = new Map(filevineEntities.map((e) => [e.id, e]));
    return {
      finchEntitiesMap: finchMap,
      filevineEntitiesMap: filevineMap,
      visibleFinchIds: new Set(finchMap.keys()),
      visibleFilevineIds: new Set(filevineMap.keys()),
    };
  }, [finchEntities, filevineEntities]);

  // Filter and flatten suggestions to show those for visible entities
  // Shows all displayable statuses (PENDING, ACCEPTED, EXECUTING, COMPLETED, FAILED)
  const { flatSuggestions } = useActionableSuggestions({
    suggestionSets: syncSuggestionsData?.suggestion_sets,
    visibleFinchEntityIds: visibleFinchIds,
    visibleFilevineEntityIds: visibleFilevineIds,
    includeReviewedAutoPushed,
  });

  const filteredFlatSuggestions = useMemo(
    () =>
      approvedEntityLinkIds
        ? flatSuggestions.filter(
            ({ set, suggestion }) =>
              // Always allow CREATE suggestions even if no entity link exists yet.
              // This supports "create counterpart" actions for currently unlinked entities.
              suggestion.action_type === 'CREATE' ||
              (!!set.entity_link_id &&
                approvedEntityLinkIds.has(set.entity_link_id)),
          )
        : flatSuggestions,
    [flatSuggestions, approvedEntityLinkIds],
  );

  const dedupedFlatSuggestions = useMemo(() => {
    const latestByPair = new Map<string, (typeof filteredFlatSuggestions)[number]>();

    filteredFlatSuggestions.forEach((entry) => {
      const pairKey = [
        entry.set.entity_link_id ?? 'no-link',
        entry.set.finch_entity_id ?? 'no-finch',
        entry.set.filevine_collection_item_id ?? 'no-filevine',
      ].join('::');

      const existing = latestByPair.get(pairKey);
      if (!existing) {
        latestByPair.set(pairKey, entry);
        return;
      }

      const existingTs = existing.suggestion.modified_at || existing.suggestion.created_at;
      const candidateTs = entry.suggestion.modified_at || entry.suggestion.created_at;

      if (candidateTs > existingTs) {
        latestByPair.set(pairKey, entry);
      }
    });

    return Array.from(latestByPair.values()).sort((a, b) => {
      const aTs = a.suggestion.modified_at || a.suggestion.created_at;
      const bTs = b.suggestion.modified_at || b.suggestion.created_at;
      return aTs > bTs ? -1 : 1;
    });
  }, [filteredFlatSuggestions]);

  const filteredPendingCount = dedupedFlatSuggestions.length;

  // Handle accept + apply suggestion (TO_FILEVINE direction)
  const handleAcceptFilevine = useCallback(
    async (
      suggestion: FilevineEntitySyncSuggestionSchema,
      modifiedPayload?: FilevineSectionPayload,
    ) => {
      // Start polling immediately to track sync progress
      startPolling();

      // Step 1: Accept the suggestion (with optional modified payload)
      await acceptSuggestion({
        matterId,
        entityType,
        suggestionId: suggestion.id,
        acceptDataSuggestionRequestSchema: {
          modified_payload: modifiedPayload,
        },
      }).unwrap();

      // Auto-push review suggestions are finalized through accept endpoint.
      if (isAutoPushPendingReview(suggestion)) {
        return;
      }

      // Step 2: Apply the suggestion (executes the sync to Filevine)
      await applySuggestion({
        matterId,
        entityType,
        suggestionId: suggestion.id,
      }).unwrap();

      // Note: Polling will continue via usePollingWhile(hasInProgressSuggestions)
      // and auto-stop when all syncs complete
    },
    [acceptSuggestion, applySuggestion, matterId, entityType, startPolling],
  );

  // Handle accept + apply suggestion (TO_FINCH direction)
  const handleAcceptFinch = useCallback(
    async (
      suggestion: FilevineEntitySyncSuggestionSchema,
      modifiedPayload?: Record<string, unknown>,
    ) => {
      // Start polling immediately to track sync progress
      startPolling();

      // Step 1: Accept the suggestion (with optional modified payload)
      await acceptSuggestion({
        matterId,
        entityType,
        suggestionId: suggestion.id,
        acceptDataSuggestionRequestSchema: {
          // Backend accepts both FilevineSectionPayload and UpsertSchema
          // based on suggestion direction, but generated types only show one
          modified_payload:
            modifiedPayload as unknown as FilevineSectionPayload,
        },
      }).unwrap();

      if (isAutoPushPendingReview(suggestion)) {
        return;
      }

      // Step 2: Apply the suggestion (executes the sync to Finch)
      await applySuggestion({
        matterId,
        entityType,
        suggestionId: suggestion.id,
      }).unwrap();

      // Note: Polling will continue via usePollingWhile(hasInProgressSuggestions)
      // and auto-stop when all syncs complete
    },
    [acceptSuggestion, applySuggestion, matterId, entityType, startPolling],
  );

  // Handle reject suggestion
  const handleReject = useCallback(
    async (suggestionId: string) => {
      await rejectSuggestion({
        matterId,
        entityType,
        suggestionId,
      }).unwrap();
    },
    [rejectSuggestion, matterId, entityType],
  );

  // Don't render if no pending suggestions
  if (!isLoading && !isError && dedupedFlatSuggestions.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Section header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-100 to-amber-50">
              <Sparkles className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">Updates</h3>
              <p className="text-sm text-zinc-500">
                Changes detected between Finch and Filevine
              </p>
            </div>
          </div>
          {filteredPendingCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700">
              {filteredPendingCount} pending
            </span>
          )}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 rounded-xl border border-zinc-200 bg-zinc-50">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          <span className="ml-2 text-zinc-500">Loading suggestions...</span>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-red-200 bg-red-50">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="mt-2 text-sm font-medium text-red-600">
            Failed to load suggestions
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="mt-4"
          >
            Try again
          </Button>
        </div>
      )}

      {/* Cards list */}
      {!isLoading && !isError && (
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
          {dedupedFlatSuggestions.map(({ suggestion, set }, index) => {
              const finchEntity = set.finch_entity_id
                ? finchEntitiesMap.get(set.finch_entity_id)
                : null;
              const filevineEntity = set.filevine_collection_item_id
                ? filevineEntitiesMap.get(set.filevine_collection_item_id)
                : null;

              // Render the appropriate card based on direction
              if (suggestion.direction === 'TO_FILEVINE') {
                return (
                  <ToFilevineSuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    parentSet={set}
                    finchEntity={finchEntity}
                    filevineEntity={filevineEntity}
                    fieldConfigs={fieldConfigs}
                    sectionConfigData={sectionConfigData}
                    canonicalSectionType={canonicalSectionType}
                    firmId={firmId}
                    onAccept={handleAcceptFilevine}
                    onReject={handleReject}
                    index={index}
                  />
                );
              }

              return (
                <ToFinchSuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  parentSet={set}
                  finchEntity={finchEntity}
                  filevineEntity={filevineEntity}
                  fieldConfigs={fieldConfigs}
                  sectionConfigData={sectionConfigData}
                  canonicalSectionType={canonicalSectionType}
                  matterId={matterId}
                  onAccept={handleAcceptFinch}
                  onReject={handleReject}
                  index={index}
                />
              );
            })}
          </AnimatePresence>

          {dedupedFlatSuggestions.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 rounded-xl border border-dashed border-zinc-200 bg-zinc-50"
            >
              <CheckCircle2 className="h-10 w-10 text-zinc-300 mx-auto" />
              <p className="mt-3 text-sm font-medium text-zinc-600">
                All caught up!
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                No pending sync suggestions
              </p>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncSuggestionCardList;
